import { SyntheticEvent } from 'react'
import {
  getS3ImageFilePath,
  getS3OriginalImageFilePath,
} from 'src/v3/utilities/image-upload'

// Quiz question images live in one of two places, and which one depends on when
// the question was authored:
//
//   - Questions authored in Create v5 / Content Pipeline upload to
//     `originals/<id>.jpg`, and a resize lambda publishes the derivative that
//     actually gets rendered, served from `/images/resized/<id>.jpg`.
//   - Questions migrated from v1 kept their flat `<id>.jpg` key, which only
//     ever existed in the public bucket, served from `/public/<id>.jpg`.
//
// Every quiz surface used to build `/images/<imageName>` unconditionally, so a
// migrated key asked the CDN for an object that isn't there — which comes back
// 403 AccessDenied, not 404 — and every image-dependent question on every
// migrated quiz rendered broken (ENG-896). Learners were being asked to read an
// ECG they couldn't see.
//
// getS3ImageFilePath picks the prefix from the key's shape, the same way the
// Create v5 course form and the Content Pipeline course fields already resolve
// thumbnails and banners. Deciding up front rather than requesting the resized
// path and falling back on error matters here: the 403 is indistinguishable from
// "the lambda hasn't finished yet", so the retrying surfaces would sit on a
// "Processing image…" spinner for ~14s before giving up on an image that was
// available the whole time.
export let questionImageSrc = (
  imageName: string | undefined,
): string | undefined => (imageName ? getS3ImageFilePath(imageName) : undefined)

// Resizing is asynchronous, so a question image uploaded moments ago can be
// missing at its resized path while the original is already in place. Swapping
// to the original keeps the question answerable instead of leaving a broken
// image. For a migrated key both helpers return `/public/<id>.jpg`, so the swap
// is a no-op — the equality guard is what stops onError from looping on an image
// that genuinely isn't there.
export let onQuestionImageError = (
  event: SyntheticEvent<HTMLElement, Event>,
  imageName: string | undefined,
): void => {
  if (!imageName) return

  let target = event.currentTarget as HTMLImageElement
  let fallback = getS3OriginalImageFilePath(imageName)
  if (target.src !== fallback) {
    // eslint-disable-next-line fp/no-mutation
    target.src = fallback
  }
}

// ImageUpload needs the base path up front to render the existing image while
// editing a question — same decision as questionImageSrc, expressed the way that
// component takes it.
export let questionImageBasePath = (
  imageName: string | undefined,
): 'images' | 'public' =>
  imageName?.includes('originals') ? 'images' : 'public'
